import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  FileButton,
  Group,
  Loader,
  Modal,
  Stack,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import {
  IconAlertCircle,
  IconFile,
  IconFileText,
  IconSettings,
  IconUpload,
} from "@tabler/icons-react";
import { useFormTemplates } from "../../context/FormTemplatesContext";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { selectedRowStyle } from "../formTemplates/selectedRowStyle";
import {
  PostAppointmentAttachmentSourceType,
  PendingPostAppointmentAttachment,
} from "../../types/servicePostAppointment";
import { FormTemplate, ClientFormSourceType } from "../../types/formTemplates";
import { SalonDocument, documentDisplayName } from "../../types/salonDocument";

interface AssignPostAppointmentAttachmentModalProps {
  opened: boolean;
  onClose: () => void;
  onAssign: (
    picks: Array<{
      source_type: PostAppointmentAttachmentSourceType;
      source_id: string;
      name: string;
    }>,
  ) => Promise<void> | void;
  isAssigning?: boolean;
  /** Composite `${source_type}:${source_id}` keys hidden from the picker. */
  existingKeys?: ReadonlySet<string>;
}

type TabKey = "custom" | "standard" | "documents";

const EMPTY_KEYS: ReadonlySet<string> = new Set();

const templateSource = (t: FormTemplate): ClientFormSourceType =>
  t.source === "custom" ? "salon_template" : "template";

const templateKey = (t: FormTemplate): string =>
  `${templateSource(t)}:${t.slug || t.id}`;

const documentKey = (d: SalonDocument): string =>
  `salon_document:${d.documentUuid}`;

export const AssignPostAppointmentAttachmentModal: React.FC<
  AssignPostAppointmentAttachmentModalProps
> = ({
  opened,
  onClose,
  onAssign,
  isAssigning = false,
  existingKeys = EMPTY_KEYS,
}) => {
  const { standardTemplates, customTemplates, fetchTemplates } =
    useFormTemplates();
  const { listSalonDocuments, uploadSalonDocument } = useApp() as {
    listSalonDocuments: (
      salonId: string,
    ) => Promise<{ documents: SalonDocument[] }>;
    uploadSalonDocument: (
      salonId: string,
      documentKey: string,
      file: File | Blob,
      contentType: string,
      metadata?: Record<string, unknown>,
      clientId?: string,
      displayName?: string,
    ) => Promise<{ document?: SalonDocument } | unknown>;
  };
  const { businessInfo } = useSalon() as {
    businessInfo: { id: string } | null;
  };

  const [tab, setTab] = useState<TabKey>("custom");
  const [search, setSearch] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [documents, setDocuments] = useState<SalonDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const refreshDocuments = useCallback(
    async (salonId: string): Promise<SalonDocument[]> => {
      const res = await listSalonDocuments(salonId);
      const next = (res?.documents ?? []).filter((d) => !d.clientId);
      setDocuments(next);
      return next;
    },
    [listSalonDocuments],
  );

  const handleUploadDocument = useCallback(
    async (file: File | null) => {
      if (!file || !businessInfo?.id) return;
      const salonId = businessInfo.id;
      const newKey =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `doc-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
      try {
        setUploading(true);
        setDocsError(null);
        await uploadSalonDocument(
          salonId,
          newKey,
          file,
          file.type || "application/octet-stream",
          undefined,
          undefined, // salon-level (no clientId) — required for post-appointment attachments
          file.name,
        );
        const next = await refreshDocuments(salonId);
        const created = next.find((d) => d.documentKey === newKey);
        if (created) {
          const compositeKey = documentKey(created);
          setSelectedKeys((prev) =>
            prev.includes(compositeKey) ? prev : [...prev, compositeKey],
          );
        }
        notifySuccess(`${file.name} is ready to attach.`, {
          title: "Document uploaded",
        });
      } catch (err) {
        setDocsError(
          err instanceof Error ? err.message : "Upload failed. Try again.",
        );
      } finally {
        setUploading(false);
      }
    },
    [businessInfo?.id, uploadSalonDocument, refreshDocuments],
  );

  useEffect(() => {
    if (!opened) {
      setSelectedKeys([]);
      setSearch("");
      setDocsError(null);
      return;
    }
    fetchTemplates().catch(() => {});
    if (!businessInfo?.id) return;
    let cancelled = false;
    (async () => {
      try {
        setDocsLoading(true);
        setDocsError(null);
        await refreshDocuments(businessInfo.id);
      } catch (err) {
        if (cancelled) return;
        setDocsError(
          err instanceof Error ? err.message : "Could not load documents.",
        );
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, businessInfo?.id, fetchTemplates, refreshDocuments]);

  // Only `post_appointment` templates are eligible — mirrors backend TAGGED_CATEGORIES.
  const standards = useMemo(
    () =>
      standardTemplates.filter(
        (t) =>
          !t.is_mock_placeholder &&
          !existingKeys.has(templateKey(t)) &&
          t.category === "post_appointment",
      ),
    [standardTemplates, existingKeys],
  );
  const customs = useMemo(
    () =>
      customTemplates.filter(
        (t) =>
          !existingKeys.has(templateKey(t)) &&
          t.category === "post_appointment",
      ),
    [customTemplates, existingKeys],
  );
  const docs = useMemo(
    () => documents.filter((d) => !existingKeys.has(documentKey(d))),
    [documents, existingKeys],
  );

  const filterTemplates = (list: FormTemplate[]) =>
    search.trim()
      ? list.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
      : list;
  const filterDocs = (list: SalonDocument[]) =>
    search.trim()
      ? list.filter((d) =>
          documentDisplayName(d).toLowerCase().includes(search.toLowerCase()),
        )
      : list;

  const activeTemplates =
    tab === "custom" ? filterTemplates(customs) : filterTemplates(standards);
  const activeDocs = filterDocs(docs);

  // Cross-tab map so selections survive tab switches.
  const nameByKey = useMemo(() => {
    const map = new Map<
      string,
      {
        name: string;
        source_type: PostAppointmentAttachmentSourceType;
        source_id: string;
      }
    >();
    for (const t of customs) {
      map.set(templateKey(t), {
        name: t.name,
        source_type: templateSource(t),
        source_id: t.slug || t.id,
      });
    }
    for (const t of standards) {
      map.set(templateKey(t), {
        name: t.name,
        source_type: templateSource(t),
        source_id: t.slug || t.id,
      });
    }
    for (const d of docs) {
      map.set(documentKey(d), {
        name: documentDisplayName(d),
        source_type: "salon_document",
        source_id: d.documentUuid,
      });
    }
    return map;
  }, [customs, standards, docs]);

  // Prune selections when their key vanishes from the pool.
  const nameByKeySignature = useMemo(
    () => Array.from(nameByKey.keys()).sort().join("|"),
    [nameByKey],
  );
  useEffect(() => {
    setSelectedKeys((prev) => {
      const next = prev.filter((k) => nameByKey.has(k));
      return next.length === prev.length ? prev : next;
    });
    // Deps: key on nameByKeySignature (stable string), NOT nameByKey (Map
    // recomputed every render — would clobber selections). exhaustive-deps
    // isn't enabled in this project, so this comment carries the contract.
  }, [nameByKeySignature]);

  const handleAssign = async () => {
    if (selectedKeys.length === 0) return;
    const picks = selectedKeys
      .map((k) => nameByKey.get(k))
      .filter((v): v is NonNullable<typeof v> => Boolean(v));
    await onAssign(picks);
  };

  const countSummary = (() => {
    if (selectedKeys.length === 0) return "Select one or more attachments";
    const buckets = { standard: 0, custom: 0, documents: 0 } as Record<
      string,
      number
    >;
    for (const k of selectedKeys) {
      if (k.startsWith("template:")) buckets.standard += 1;
      else if (k.startsWith("salon_template:")) buckets.custom += 1;
      else if (k.startsWith("salon_document:")) buckets.documents += 1;
    }
    return [
      buckets.custom ? `${buckets.custom} custom` : null,
      buckets.standard ? `${buckets.standard} standard` : null,
      buckets.documents
        ? `${buckets.documents} document${buckets.documents > 1 ? "s" : ""}`
        : null,
    ]
      .filter(Boolean)
      .join(" + ")
      .concat(" selected");
  })();

  const buttonLabel =
    selectedKeys.length > 1
      ? `Assign ${selectedKeys.length} attachments`
      : "Assign";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={700}>Assign attachments</Text>}
      size="lg"
      centered
    >
      <Stack gap="md">
        <Tabs value={tab} onChange={(v) => v && setTab(v as TabKey)}>
          <Tabs.List>
            <Tabs.Tab value="custom">Custom templates</Tabs.Tab>
            <Tabs.Tab value="standard">Standard templates</Tabs.Tab>
            <Tabs.Tab value="documents">Documents</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        <TextInput
          placeholder={
            tab === "documents" ? "Search documents by name" : "Search by name"
          }
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          size="xs"
        />

        {tab === "documents" && docsError && (
          <Alert color="red" icon={<IconAlertCircle size={14} />}>
            {docsError}
          </Alert>
        )}

        {tab === "documents" && (
          <Group justify="flex-end" align="center">
            <FileButton
              onChange={handleUploadDocument}
              accept="application/pdf,image/png,image/jpeg,image/heic,image/heif,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            >
              {(props) => (
                <Button
                  {...props}
                  size="xs"
                  variant="light"
                  color="var(--brand-teal)"
                  leftSection={<IconUpload size={14} />}
                  loading={uploading}
                  disabled={!businessInfo?.id || uploading}
                >
                  {uploading ? "Uploading…" : "Upload document"}
                </Button>
              )}
            </FileButton>
          </Group>
        )}

        <Box
          style={{
            maxHeight: 360,
            overflowY: "auto",
            padding: 4,
            border: "1px solid var(--mantine-color-gray-2)",
            borderRadius: 6,
          }}
        >
          {tab === "documents" ? (
            docsLoading ? (
              <Group justify="center" py="md">
                <Loader size="xs" />
              </Group>
            ) : activeDocs.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" p="md">
                {search.trim()
                  ? "No documents match."
                  : documents.length > 0
                    ? "All documents are already attached."
                    : "No documents uploaded yet. Use Upload document above to add one."}
              </Text>
            ) : (
              <Checkbox.Group value={selectedKeys} onChange={setSelectedKeys}>
                <Stack gap={0}>
                  {activeDocs.map((d) => {
                    const key = documentKey(d);
                    const name = documentDisplayName(d);
                    return (
                      <Box
                        key={key}
                        p="xs"
                        onClick={() =>
                          setSelectedKeys((prev) =>
                            prev.includes(key)
                              ? prev.filter((k) => k !== key)
                              : [...prev, key],
                          )
                        }
                        style={selectedRowStyle(selectedKeys.includes(key))}
                      >
                        <Group gap="xs" wrap="nowrap" align="flex-start">
                          <Checkbox
                            value={key}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <IconFile
                            size={18}
                            color="var(--mantine-color-blue-6)"
                          />
                          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={600} truncate>
                              {name}
                            </Text>
                            {d.currentContentType && (
                              <Text size="xs" c="dimmed">
                                {d.currentContentType}
                              </Text>
                            )}
                          </Stack>
                        </Group>
                      </Box>
                    );
                  })}
                </Stack>
              </Checkbox.Group>
            )
          ) : activeTemplates.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" p="md">
              {tab === "custom"
                ? search.trim()
                  ? "No custom templates match."
                  : customTemplates.length > 0
                    ? "All custom templates are already attached."
                    : "No custom templates yet."
                : search.trim()
                  ? "No standard templates match."
                  : "All standard templates are already attached."}
            </Text>
          ) : (
            <Checkbox.Group value={selectedKeys} onChange={setSelectedKeys}>
              <Stack gap={0}>
                {activeTemplates.map((t) => {
                  const key = templateKey(t);
                  const Icon =
                    t.source === "custom" ? IconSettings : IconFileText;
                  return (
                    <Box
                      key={key}
                      p="xs"
                      onClick={() =>
                        setSelectedKeys((prev) =>
                          prev.includes(key)
                            ? prev.filter((k) => k !== key)
                            : [...prev, key],
                        )
                      }
                      style={selectedRowStyle(selectedKeys.includes(key))}
                    >
                      <Group gap="xs" wrap="nowrap" align="flex-start">
                        <Checkbox
                          value={key}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Icon
                          size={18}
                          color={
                            t.source === "custom"
                              ? "var(--brand-burnt-orange)"
                              : "var(--mantine-color-teal-6)"
                          }
                        />
                        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                          <Group gap={4} wrap="nowrap">
                            <Text size="sm" fw={600} truncate>
                              {t.name}
                            </Text>
                            <Badge
                              size="xs"
                              variant="light"
                              color="gray"
                              style={{ textTransform: "none" }}
                            >
                              v{t.version}
                            </Badge>
                          </Group>
                          {t.description && (
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {t.description}
                            </Text>
                          )}
                          <Text size="xs" c="dimmed">
                            {t.field_count} fields
                          </Text>
                        </Stack>
                      </Group>
                    </Box>
                  );
                })}
              </Stack>
            </Checkbox.Group>
          )}
        </Box>

        <Group justify="space-between" align="center">
          <Text size="xs" c="dimmed">
            {countSummary}
          </Text>
          <Group gap="xs">
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              disabled={selectedKeys.length === 0}
              loading={isAssigning}
              onClick={handleAssign}
            >
              {buttonLabel}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export function makePendingAttachment(
  pick: {
    source_type: PostAppointmentAttachmentSourceType;
    source_id: string;
    name: string;
  },
  nextSortOrder: number,
): PendingPostAppointmentAttachment {
  return {
    tempId:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `tmp-${Math.random().toString(36).slice(2)}`,
    source_type: pick.source_type,
    source_id: pick.source_id,
    name: pick.name,
    sort_order: nextSortOrder,
  };
}

export default AssignPostAppointmentAttachmentModal;
